import React, { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  Input,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
  Spinner,
  Table,
} from "reactstrap";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast, ToastContainer } from "react-toastify";
import BreadCrumb from "../../../Components/Common/BreadCrumb";
import DismissiblePageTip from "../../../Components/Common/DismissiblePageTip";
import PageHelpLink from "../../../Components/Common/PageHelpLink";
import { helpUrlForRoute } from "../../../constants/helpRouteMap";
import {
  apiErrorMessage,
  CaptureForm,
  deleteForm,
  getForms,
  updateForm,
} from "../../../helpers/forms_helper";

const PER_PAGE = 25;
const SEARCH_DEBOUNCE_MS = 350;

/**
 * Lista de formulários de captura (DOC 03 § 4.13).
 */
const FormList = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  document.title = t("forms.documentTitle");

  const helpHref = helpUrlForRoute("/forms", i18n.language || "pt-BR");

  const [items, setItems] = useState<CaptureForm[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [page, setPage] = useState(1);
  const [lastPage, setLastPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [deleting, setDeleting] = useState<CaptureForm | null>(null);
  const [busyId, setBusyId] = useState<number | null>(null);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setDebouncedSearch(search.trim());
      setPage(1);
    }, SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(timer);
  }, [search]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await getForms({
        search: debouncedSearch || undefined,
        page,
        per_page: PER_PAGE,
      });
      setItems(res.data || []);
      setLastPage(res.meta?.last_page || 1);
      setTotal(res.meta?.total || 0);
    } catch (e) {
      setError(apiErrorMessage(e, t("forms.error.load")));
    } finally {
      setLoading(false);
    }
  }, [debouncedSearch, page, t]);

  useEffect(() => {
    void load();
  }, [load]);

  const toggleStatus = async (form: CaptureForm) => {
    const next = form.status === "active" ? "paused" : "active";
    setBusyId(form.id);
    try {
      await updateForm(form.id, { status: next });
      toast.success(
        next === "paused"
          ? t("forms.success.paused")
          : t("forms.success.activated")
      );
      await load();
    } catch (e) {
      setError(apiErrorMessage(e, t("forms.error.save")));
    } finally {
      setBusyId(null);
    }
  };

  const confirmDelete = async () => {
    if (!deleting) return;
    setBusyId(deleting.id);
    try {
      await deleteForm(deleting.id);
      toast.success(t("forms.success.deleted"));
      setDeleting(null);
      await load();
    } catch (e) {
      setError(apiErrorMessage(e, t("forms.error.delete")));
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="page-content">
      <Container fluid>
        <BreadCrumb
          title={t("forms.title")}
          pageTitle={t("menu.automation")}
          actions={
            helpHref ? (
              <PageHelpLink href={helpHref} label={t("forms.help")} />
            ) : undefined
          }
        />
        <DismissiblePageTip tipId="forms-intro">
          {t("forms.tip")}
        </DismissiblePageTip>

        {error && (
          <Alert color="danger" toggle={() => setError(null)}>
            {error}
          </Alert>
        )}

        <Row>
          <Col lg={12}>
            <Card>
              <CardHeader className="d-flex flex-wrap gap-2 justify-content-between align-items-center">
                <div className="d-flex gap-2 align-items-center flex-grow-1">
                  <Input
                    type="search"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder={t("forms.searchPlaceholder")}
                    style={{ maxWidth: 280 }}
                  />
                  <span className="text-muted small">
                    {t("forms.total", { count: total })}
                  </span>
                </div>
                <Button color="primary" onClick={() => navigate("/forms/new")}>
                  {t("forms.create")}
                </Button>
              </CardHeader>
              <CardBody>
                {loading ? (
                  <div className="text-center py-5">
                    <Spinner color="primary" />
                  </div>
                ) : items.length === 0 ? (
                  <div className="text-center text-muted py-5">
                    <p className="mb-3">{t("forms.empty")}</p>
                    <Button
                      color="primary"
                      outline
                      onClick={() => navigate("/forms/new")}
                    >
                      {t("forms.create")}
                    </Button>
                  </div>
                ) : (
                  <>
                    <div className="table-responsive">
                      <Table className="align-middle table-nowrap mb-0">
                        <thead className="table-light">
                          <tr>
                            <th>{t("forms.col.name")}</th>
                            <th>{t("forms.col.status")}</th>
                            <th>{t("forms.col.doi")}</th>
                            <th>{t("forms.col.submissions")}</th>
                            <th>{t("forms.col.confirmed")}</th>
                            <th>{t("forms.col.conversion")}</th>
                            <th>{t("forms.col.actions")}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {items.map((form) => (
                            <tr key={form.id}>
                              <td>
                                <Link
                                  to={`/forms/${form.id}`}
                                  className="fw-medium"
                                >
                                  {form.name}
                                </Link>
                              </td>
                              <td>
                                <Badge
                                  color={
                                    form.status === "active"
                                      ? "success"
                                      : "secondary"
                                  }
                                  className="text-uppercase"
                                >
                                  {t(`forms.status.${form.status}`)}
                                </Badge>
                              </td>
                              <td>
                                {form.double_opt_in
                                  ? t("forms.doi.on")
                                  : t("forms.doi.off")}
                              </td>
                              <td>
                                <div>{form.stats_summary?.submissions ?? "—"}</div>
                                {(form.stats_summary?.submissions ?? 0) === 0 &&
                                  (form.stats_summary?.confirmed ?? 0) === 0 && (
                                    <Alert
                                      color="warning"
                                      className="py-1 px-2 mb-0 mt-1 small"
                                    >
                                      {t("forms.stats.listEmptyCta")}{" "}
                                      <Link
                                        to={`/forms/${form.id}/embed`}
                                        className="alert-link"
                                      >
                                        {t("forms.stats.listEmptyLink")}
                                      </Link>
                                    </Alert>
                                  )}
                                {form.stats_summary?.by_source && (
                                  <SourceHint
                                    bySource={form.stats_summary.by_source}
                                    t={t}
                                  />
                                )}
                              </td>
                              <td>{form.stats_summary?.confirmed ?? "—"}</td>
                              <td>
                                {form.stats_summary?.conversion_pct != null
                                  ? `${form.stats_summary.conversion_pct}%`
                                  : "—"}
                              </td>
                              <td>
                                <div className="d-flex flex-wrap gap-1">
                                  <Button
                                    color="soft-primary"
                                    size="sm"
                                    tag={Link}
                                    to={`/forms/${form.id}`}
                                  >
                                    {t("forms.edit")}
                                  </Button>
                                  <Button
                                    color="soft-info"
                                    size="sm"
                                    tag={Link}
                                    to={`/forms/${form.id}/embed`}
                                  >
                                    {t("forms.embed")}
                                  </Button>
                                  <Button
                                    color="soft-warning"
                                    size="sm"
                                    disabled={busyId === form.id}
                                    onClick={() => void toggleStatus(form)}
                                  >
                                    {form.status === "active"
                                      ? t("forms.pause")
                                      : t("forms.activate")}
                                  </Button>
                                  <Button
                                    color="soft-danger"
                                    size="sm"
                                    disabled={busyId === form.id}
                                    onClick={() => setDeleting(form)}
                                  >
                                    {t("forms.delete")}
                                  </Button>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </div>
                    {lastPage > 1 && (
                      <div className="d-flex justify-content-between align-items-center mt-3">
                        <Button
                          color="light"
                          size="sm"
                          disabled={page <= 1}
                          onClick={() => setPage((p) => Math.max(1, p - 1))}
                        >
                          {t("forms.prev")}
                        </Button>
                        <span className="text-muted small">
                          {page} / {lastPage}
                        </span>
                        <Button
                          color="light"
                          size="sm"
                          disabled={page >= lastPage}
                          onClick={() =>
                            setPage((p) => Math.min(lastPage, p + 1))
                          }
                        >
                          {t("forms.next")}
                        </Button>
                      </div>
                    )}
                  </>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>

      <Modal isOpen={!!deleting} toggle={() => setDeleting(null)} centered>
        <ModalHeader toggle={() => setDeleting(null)}>
          {t("forms.deleteTitle")}
        </ModalHeader>
        <ModalBody>
          {t("forms.deleteConfirm", { name: deleting?.name || "" })}
        </ModalBody>
        <ModalFooter>
          <Button color="light" onClick={() => setDeleting(null)}>
            {t("forms.cancel")}
          </Button>
          <Button
            color="danger"
            disabled={busyId === deleting?.id}
            onClick={() => void confirmDelete()}
          >
            {t("forms.delete")}
          </Button>
        </ModalFooter>
      </Modal>

      <ToastContainer autoClose={3000} />
    </div>
  );
};

function SourceHint({
  bySource,
  t,
}: {
  bySource: { embed: number; landing: number; api: number };
  t: (key: string) => string;
}) {
  const parts: string[] = [];
  if (bySource.embed > 0) {
    parts.push(`${t("forms.stats.source.embed")}: ${bySource.embed}`);
  }
  if (bySource.landing > 0) {
    parts.push(`${t("forms.stats.source.landing")}: ${bySource.landing}`);
  }
  if (bySource.api > 0) {
    parts.push(`${t("forms.stats.source.api")}: ${bySource.api}`);
  }
  if (parts.length === 0) return null;

  return (
    <div className="text-muted small mt-1">{parts.join(" · ")}</div>
  );
}

export default FormList;
